import { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import SectionDivider from 'modules/common/components/SectionDivider';

import {
  Wrapper,
  SelectWrapper,
  Select,
  TextInput,
  Icon,
  Envelop,
  EnvelopItem,
  SelectedItemsWrapper,
  SelectedItem,
  SelectedItemCloseIcon,
} from './MultiSelectionDropdownMenu.style';

// ###############################################################
// <------------------------ Constants -------------------------->
// ###############################################################
// ###############################################################
// <---------------------- End Constants ------------------------>
// ###############################################################
const MultiSelectionDropdownMenu = ({
  dropdownMenuItemsList,
  dropdownMenuSelectedArr,
  dropdownMenuPlaceholder,
  disabled,
  handleDropdownMenuChange,
  objectMapper,
}) => {
  // ###############################################################
  // <------------------------- Globals --------------------------->
  // ###############################################################
  const dropdownMenuWrapperRef = useRef(null);
  // ###############################################################
  // <----------------------- End Globals ------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------ useState --------------------------->
  // ###############################################################
  const [isMenuEnvelopOpen, setIsMenuEnvelopOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [filteredDropdownMenuItemsList, setFilteredDropdownMenuItemsList] = useState([
    ...dropdownMenuItemsList,
  ]);
  // ###############################################################
  // <---------------------- End useState ------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------ useEffect -------------------------->
  // ###############################################################
  useEffect(() => {
    const handleClickOutside = event => {
      if (
        dropdownMenuWrapperRef.current &&
        dropdownMenuWrapperRef.current.contains(event.target) === false
      ) {
        setIsMenuEnvelopOpen(false);
        setSearchText('');
      }
    };
    document.addEventListener('click', handleClickOutside, true);

    return () => {
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, [dropdownMenuWrapperRef]);

  useEffect(() => {
    if (dropdownMenuItemsList?.length > 0) {
      const filteredData = dropdownMenuItemsList.filter(item =>
        item[objectMapper.name].toLowerCase().includes(searchText.toLowerCase()),
      );
      setFilteredDropdownMenuItemsList([...filteredData]);
    }
  }, [dropdownMenuItemsList, objectMapper.name, searchText]);

  // ###############################################################
  // <---------------------- End useEffect ------------------------>
  // ###############################################################
  // ###############################################################
  // <--------------------- rest of hooks ------------------------->
  // ###############################################################
  // ###############################################################
  // <-------------------- End rest of hooks ---------------------->
  // ###############################################################
  // ###############################################################
  // <-------------------------- Logic ---------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------ End Logic -------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------- Actions --------------------------->
  // ###############################################################
  // ###############################################################
  // <----------------------- End Actions ------------------------->
  // ###############################################################
  // ###############################################################
  // <----------------------- UI Handlers ------------------------->
  // ###############################################################
  const handleMenuSelectClick = () => {
    if (disabled) return;

    setIsMenuEnvelopOpen(prevState => !prevState);
  };

  const handleDropdownMenuItemClick = selectedObj => {
    setIsMenuEnvelopOpen(false);

    if (
      dropdownMenuSelectedArr.some(selectedItem => selectedItem.key === selectedObj.key) === false
    ) {
      const updateSelectedItems = [...dropdownMenuSelectedArr, selectedObj];
      handleDropdownMenuChange(updateSelectedItems);
    }
  };

  const handleSelectedItemItemDeleteIconClick = selectedItem => {
    const updateSelectedItems = dropdownMenuSelectedArr.filter(
      result => result.key !== selectedItem.key,
    );

    handleDropdownMenuChange(updateSelectedItems);
  };
  // ###############################################################
  // <--------------------- End UI Handlers ----------------------->
  // ###############################################################
  // ###############################################################
  // <-------------------------- Render --------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------ End Render ------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------- Return UI ------------------------->
  // ###############################################################
  return (
    <Wrapper>
      <SelectWrapper ref={dropdownMenuWrapperRef}>
        <Select
          data-testid="dropdown-menu-select"
          disabled={disabled}
          onClick={handleMenuSelectClick}
        >
          <TextInput
            data-testid="dropdown-menu-text"
            type="text"
            value={searchText}
            placeholder={dropdownMenuPlaceholder}
            onChange={event => setSearchText(event.target.value)}
          />
          {searchText && (
            <Icon
              icon={'"\\e91f"'}
              onClick={() => {
                setSearchText('');
                setFilteredDropdownMenuItemsList([...dropdownMenuItemsList]);
              }}
            />
          )}
          <Icon icon={'"\\e913"'} data-testid="dropdown-menu-icon" />
        </Select>
        {isMenuEnvelopOpen && (
          <Envelop data-testid="dropdown-menu-envelope">
            {filteredDropdownMenuItemsList.map(menuItem => (
              <EnvelopItem
                data-testid={menuItem[objectMapper.key]}
                key={menuItem[objectMapper.key]}
                onClick={() =>
                  handleDropdownMenuItemClick({
                    key: menuItem[objectMapper.key],
                    name: menuItem[objectMapper.name],
                  })
                }
              >
                {menuItem[objectMapper.name]}
              </EnvelopItem>
            ))}
          </Envelop>
        )}
      </SelectWrapper>
      <SectionDivider gap={4} />
      <SelectedItemsWrapper>
        {dropdownMenuSelectedArr.map(selectedItem => (
          <SelectedItem key={selectedItem.key}>
            {selectedItem.name}
            <SelectedItemCloseIcon
              onClick={() => handleSelectedItemItemDeleteIconClick(selectedItem)}
            />
          </SelectedItem>
        ))}
      </SelectedItemsWrapper>
    </Wrapper>
  );
  // ###############################################################
  // <----------------------- End Return UI ----------------------->
  // ###############################################################
};

MultiSelectionDropdownMenu.propTypes = {
  dropdownMenuItemsList: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  dropdownMenuSelectedArr: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string,
      name: PropTypes.string,
    }),
  ).isRequired,
  dropdownMenuPlaceholder: PropTypes.string,
  disabled: PropTypes.bool,
  handleDropdownMenuChange: PropTypes.func.isRequired,
  objectMapper: PropTypes.shape({
    key: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    name: PropTypes.string,
  }).isRequired,
};

MultiSelectionDropdownMenu.defaultProps = {
  dropdownMenuPlaceholder: '',
  disabled: false,
};

export default MultiSelectionDropdownMenu;
